import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Activity, CheckCircle2, AlertTriangle, XCircle, Loader2, Clock,
} from 'lucide-react';
import { apiFetch } from '../api';

/**
 * StatusPage — public system status. No login required.
 *
 * Layout: simple, honest, no marketing.
 * - Big banner at the top: "All systems operational" / "Some systems degraded" / "Major outage"
 * - Three rows below: API / Stealth (Datames) / Classic engine
 *   - Each row shows a colored dot, the component name, and its status
 * - Footer: last-checked timestamp + auto-refresh notice
 * - Back link to the dashboard (or sign-in if not logged in)
 *
 * Refreshes every 60s to match the server-side cache. No manual refresh
 * button — the server caches the response anyway.
 */

const REFRESH_INTERVAL_MS = 60_000;

const STATUS_META = {
  operational: {
    label: 'Operational',
    color: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-500',
    ring: 'ring-emerald-500/30',
    icon: CheckCircle2,
    description: 'Working normally',
  },
  degraded: {
    label: 'Degraded',
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-500',
    ring: 'ring-amber-500/30',
    icon: AlertTriangle,
    description: 'Slow or partial — requests may still work',
  },
  outage: {
    label: 'Outage',
    color: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-500',
    ring: 'ring-red-500/30',
    icon: XCircle,
    description: 'Not responding',
  },
};

const BANNER_META = {
  operational: {
    title: 'All systems operational',
    body: 'Every service is responding normally.',
    className: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300',
    icon: CheckCircle2,
    iconClass: 'text-emerald-500',
  },
  degraded: {
    title: 'Some systems are degraded',
    body: 'One or more services are slow or partially down. Your requests may still work, but expect higher latency or occasional failures.',
    className: 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300',
    icon: AlertTriangle,
    iconClass: 'text-amber-500',
  },
  outage: {
    title: 'Major outage',
    body: 'A critical service is down. New requests will fail until this is resolved.',
    className: 'bg-red-500/10 border-red-500/30 text-red-700 dark:text-red-300',
    icon: XCircle,
    iconClass: 'text-red-500',
  },
};

function formatRelative(epochSeconds) {
  if (!epochSeconds) return '—';
  const delta = Math.max(0, Math.floor((Date.now() / 1000) - epochSeconds));
  if (delta < 5) return 'just now';
  if (delta < 60) return `${delta}s ago`;
  if (delta < 3600) return `${Math.floor(delta / 60)}m ago`;
  if (delta < 86400) return `${Math.floor(delta / 3600)}h ago`;
  return new Date(epochSeconds * 1000).toLocaleString();
}

function StatusDot({ status, pulse = true }) {
  const meta = STATUS_META[status] || STATUS_META.operational;
  return (
    <span
      className={`relative inline-flex h-2.5 w-2.5 rounded-full ${meta.bg}`}
      aria-label={meta.label}
    >
      {pulse && status === 'operational' && (
        <span className={`absolute inset-0 rounded-full ${meta.bg} opacity-60 animate-ping`} />
      )}
    </span>
  );
}

function ComponentRow({ component }) {
  const meta = STATUS_META[component.status] || STATUS_META.operational;
  const Icon = meta.icon;
  return (
    <div className="flex items-center gap-4 px-5 py-4 border-b border-border last:border-b-0">
      <StatusDot status={component.status} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-fg">{component.name}</div>
        <div className="text-xs text-fg-muted mt-0.5">{meta.description}</div>
      </div>
      <div className={`flex items-center gap-1.5 text-sm font-medium ${meta.color}`}>
        <Icon className="h-4 w-4" />
        <span>{meta.label}</span>
      </div>
    </div>
  );
}

export default function StatusPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [, setTick] = useState(0); // forces the "checked Xs ago" label to update
  const aliveRef = useRef(true);

  const load = useCallback(async () => {
    try {
      // No auth — /v1/status is public. apiFetch returns parsed JSON directly.
      const json = await apiFetch('/v1/status');
      if (!aliveRef.current) return;
      // Normalize the backend's status strings to the keys used by STATUS_META
      // / BANNER_META. Backend returns 'operational' for healthy systems,
      // 'degraded' when something's down, or 'outage' on hard errors
      // (see backend/app/routers/health.py).
      const status = json.status === 'operational' ? 'operational'
                   : json.status === 'degraded' ? 'degraded'
                   : 'outage';
      setData({ ...json, status, components: json.components || [] });
      setError(null);
    } catch (e) {
      if (!aliveRef.current) return;
      setError(e.message || 'Failed to load status');
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    load();
    const id = setInterval(load, REFRESH_INTERVAL_MS);
    // Tick once a second so the "checked Xs ago" label stays fresh
    const tickId = setInterval(() => setTick((n) => n + 1), 1000);
    return () => {
      aliveRef.current = false;
      clearInterval(id);
      clearInterval(tickId);
    };
  }, [load]);

  const banner = data ? BANNER_META[data.status] : null;
  const BannerIcon = banner ? banner.icon : Activity;

  return (
    <div className="min-h-screen bg-bg text-fg">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2.5">
            <img
              src="/logo.png"
              alt="Datames"
              className="h-8 w-8 rounded-md object-cover shadow-[0_0_0_1px_hsl(var(--border))]"
              draggable="false"
            />
            <div className="leading-tight">
              <div className="text-sm font-semibold text-fg">Datames</div>
              <div className="text-2xs text-fg-muted uppercase tracking-wider">System status</div>
            </div>
          </div>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-fg transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            <span>Back</span>
          </Link>
        </div>

        {/* Big banner */}
        {error ? (
          <div className="rounded-lg border bg-red-500/10 border-red-500/30 text-red-700 dark:text-red-300 p-5 mb-6">
            <div className="flex items-start gap-3">
              <XCircle className="h-5 w-5 mt-0.5 shrink-0 text-red-500" />
              <div>
                <div className="text-base font-semibold">Can't reach the status API</div>
                <div className="text-sm mt-1 opacity-90">
                  {error}. The status page will keep trying — refreshing every minute.
                </div>
              </div>
            </div>
          </div>
        ) : loading && !data ? (
          <div className="rounded-lg border border-border bg-surface p-5 mb-6 flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-fg-muted" />
            <span className="text-sm text-fg-muted">Checking service health…</span>
          </div>
        ) : banner ? (
          <div className={`rounded-lg border p-5 mb-6 ${banner.className}`}>
            <div className="flex items-start gap-3">
              <BannerIcon className={`h-5 w-5 mt-0.5 shrink-0 ${banner.iconClass}`} />
              <div>
                <div className="text-base font-semibold">{banner.title}</div>
                <div className="text-sm mt-1 opacity-90">{banner.body}</div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Components list */}
        <div className="rounded-lg border border-border bg-surface overflow-hidden">
          <div className="px-5 py-3 border-b border-border bg-surface-2/50 flex items-center justify-between">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-fg-muted">
              Components
            </h2>
            <span className="text-2xs text-fg-subtle">Updated every minute</span>
          </div>
          {data?.components?.length ? (
            data.components.map((c) => <ComponentRow key={c.id} component={c} />)
          ) : (
            <div className="px-5 py-8 text-center text-sm text-fg-muted">
              {loading ? 'Loading…' : 'No data'}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 text-xs text-fg-subtle">
          <div className="flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" />
            <span>
              Checked {data ? formatRelative(data.checked_at) : '—'}
            </span>
          </div>
          <div>
            Need help?{' '}
            <a
              href="mailto:support@datames.com"
              className="text-fg-muted hover:text-fg underline underline-offset-2 transition-colors"
            >
              support@datames.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
